import * as React from 'react';
import { useQuery } from '@tanstack/react-query';

import { PAYMENT_PROVIDERS } from '@medipass/utils/constants';
import sdk from '@medipass/web-sdk';

interface ApplePayButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  amount?: string;
  onError?: (...args: Array<any>) => any;
  onSuccess?: (...args: Array<any>) => any;
}

export default function ApplePayButton({ amount = '', onSuccess, onError, ...restButtonProps }: ApplePayButtonProps) {
  const { canPay, paymentRequest } = useApplePayButton({
    amount,
  });

  const handleClick = React.useCallback(() => {
    const session = new window.ApplePaySession(3, paymentRequest);

    session.onvalidatemerchant = async function (event) {
      try {
        const merchantSession = await sdk.applePay.performMerchantValidation(
          {
            validationUrl: event.validationURL,
            displayName: 'Tyro Health',
          },
          {
            requestConfig: {
              tokenType: 'session',
            },
          },
        );
        session.completeMerchantValidation(merchantSession);
      } catch (error) {
        onError?.(error);
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
      }
    };

    session.onpaymentauthorized = function (event) {
      if (!event?.payment?.token) {
        onError?.('No payment token found');
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
        return;
      }

      onSuccess?.({
        nonce: JSON.stringify(event?.payment?.token.paymentData),
        paymentProvider: PAYMENT_PROVIDERS.APPLE_PAY,
      });
      session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
    };

    session.begin();
  }, [onError, onSuccess, paymentRequest]);

  return canPay ? (
    <React.Fragment>
      <button
        type="button"
        onClick={handleClick}
        className="pointer-events-auto h-10 min-h-[3rem] min-w-full rounded border-0 bg-black bg-contain bg-center bg-no-repeat bg-origin-content px-6 py-3 shadow-none"
        style={{ backgroundImage: '-webkit-named-image(apple-pay-logo-white)' }}
        {...restButtonProps}
      />
    </React.Fragment>
  ) : null;
}

export function useApplePayButton({ amount = '' }: { amount?: string }) {
  const isSupported = React.useMemo(() => {
    try {
      const isSupported =
        window.ApplePaySession && window.ApplePaySession.supportsVersion(3) && window.ApplePaySession.canMakePayments();
      return isSupported;
    } catch (err) {
      return false;
    }
  }, []);

  const getNonceQuery = useQuery({
    queryKey: ['apple-pay-nonce', amount],
    queryFn: async () => {
      const data = await sdk.payments.getNonce({
        amount: amount.replace('$', ''),
        paymentProvider: PAYMENT_PROVIDERS.APPLE_PAY,
        isSelfCheckoutSession: true,
      });
      return data;
    },
    enabled: isSupported,
  });

  const applePayInstance = getNonceQuery?.data?.applePayInstance;

  const canMakeApplePayment = getNonceQuery?.data?.canMakeApplePayment;

  const paymentRequest = getNonceQuery?.data?.paymentRequest;

  return {
    applePayInstance,
    canPay: isSupported && canMakeApplePayment,
    paymentRequest,
  };
}
